import { branchRegionsApi } from '@texas/api/endpoints/branchRegionsApi';
import { compositionGroupApi } from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { productGroupsApi } from '@texas/api/endpoints/metadata/productGroupsApi';
import { templateRangeTiersApi } from '@texas/api/endpoints/metadata/templateRangeTiersApi';
import { treatmentsApi } from '@texas/api/endpoints/metadata/treatmentsApi';
import { vdcApi, VdcPricing } from '@texas/api/endpoints/vdcApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';
import { useEffect, useState } from 'react';

export interface VdcFormValues {
  productGroupId: number | null;
  width: number;
  length: number;
  tierRangeId: number | null;
  compositionGroupId: number | null;
  treatmentIds: number[] | null;
}

export interface AddedValue extends VdcFormValues {
  pricing?: VdcPricing;
}

export function useVdcConfiguration() {
  const {
    data: productGroups,
    refetch: refetchProductGroups,
    loading: loadingProductGroups,
  } = useApiResource(productGroupsApi.getAll);
  const {
    branchesData: [branches, getBranches, loadingBranches],
  } = useMetadata();
  const {
    data: templateRangeTiers,
    refetch: refetchTemplateRangeTiers,
    loading: loadingTemplateRangeTiers,
  } = useApiResource(templateRangeTiersApi.getAll);
  const {
    data: branchRegions,
    refetch: refetchBranchRegions,
    loading: loadingBranchRegions,
  } = useApiResource(branchRegionsApi.getAll);
  const {
    data: templates,
    refetch: refetchTemplates,
    loading: loadingTemplates,
  } = useApiResource(compositionGroupApi.templatesOverview);
  const {
    request: calculatePricing,
    loading: loadingPricing,
    error: errorPricing,
  } = useApiRequest(vdcApi.calculatePricing);
  const {
    data: treatments,
    refetch: refetchTreatments,
    loading: loadingTreatments,
  } = useApiResource(treatmentsApi.getAll);

  useEffect(() => {
    refetchTemplateRangeTiers({
      searchTerm: '',
      includeArchived: false,
    });
    refetchBranchRegions('');
    refetchTemplates({ isReady: true });
    getBranches();
    refetchProductGroups({
      sortBy: '',
      sortDesc: false,
      searchTerm: '',
      includeArchived: false,
      vdcSupport: true,
    });
    refetchTreatments({
      sortBy: '',
      sortDesc: false,
      searchTerm: '',
      includeArchived: false,
      vdcSupport: true,
      preciseIdFiltering: false,
      productGroupId: null,
    });
  }, [
    refetchTemplateRangeTiers,
    refetchBranchRegions,
    refetchTemplates,
    refetchProductGroups,
    refetchTreatments,
    getBranches,
  ]);

  const [addedValues, setAddedValues] = useState<AddedValue[]>([]);
  const [configuredBranches, setConfiguredBranches] = useState<number[]>([]);

  async function addValues(values: VdcFormValues) {
    if (
      !values.productGroupId ||
      configuredBranches.length === 0 ||
      !values.tierRangeId ||
      !values.compositionGroupId
    ) {
      return;
    }

    const response = await calculatePricing({
      productGroupId: values.productGroupId,
      branchIds: configuredBranches,
      tierRangeId: values.tierRangeId,
      templateId: values.compositionGroupId,
      labelWidth: values.width,
      labelHeight: values.length,
    });

    if (!response.hasError) {
      setAddedValues((prev) => [
        ...prev,
        { ...values, pricing: response.data },
      ]);
    }
  }

  async function refreshPricing(branchIds: number[]) {
    const newValues = [];
    for (let i = 0; i < addedValues.length; i++) {
      const val = addedValues[i];
      const response = await calculatePricing({
        productGroupId: val.productGroupId!,
        branchIds: branchIds,
        tierRangeId: val.tierRangeId!,
        templateId: val.compositionGroupId!,
        labelWidth: val.width,
        labelHeight: val.length,
      });

      if (!response.hasError) {
        newValues.push({ ...val, pricing: response.data });
      } else {
        newValues.push(val);
      }
    }
    setAddedValues(newValues);
  }

  async function updateBranches(branchIds: number[]) {
    setConfiguredBranches(branchIds);
    await refreshPricing(branchIds);
  }

  function removeValue(index: number) {
    setAddedValues((prev) => prev.filter((_, i) => i !== index));
  }

  return {
    productGroups,
    templateRangeTiers,
    branchRegions,
    templates,
    branches,
    treatments,
    calculatePricing,
    loadingProductGroups,
    loadingTemplateRangeTiers,
    loadingBranchRegions,
    loadingTemplates,
    loadingPricing,
    loadingBranches,
    loadingTreatments,
    error: errorPricing,
    addedValues,
    addValues,
    removeValue,
    configuredBranches,
    updateBranches,
  };
}
